import type { AssetId } from '../../assets/AssetTypes';
import type { CollectibleSpawnPoint } from '../segments/SegmentDefinition';
import type { DecorationSpacing } from './DecorationCatalog';

/**
 * Result of populating one segment: which socket receives what.
 * Pure data – managers turn it into gameplay instances, presenters into visuals.
 */
export interface ObstaclePlacement {
  readonly spawnPointId: string;
  readonly obstacleId: string;
  /** Visual yaw (radians), e.g. guide signs turned so their arrows point to a free lane. */
  readonly yaw?: number;
}

export interface CollectiblePlacement {
  /** A spawn point of the segment – or the id of `point`. */
  readonly spawnPointId: string;
  readonly collectibleId: string;
  /** Added to the spawn point height (e.g. arcs over jump obstacles). */
  readonly heightOffset: number;
  /** Size factor of visual and pickup volume (set-piece rewards, SegmentDefinition.fixedCollectibles) – default 1. */
  readonly scale?: number;
  /**
   * A spawn point of the populator's own (segment-local), where the segment has none: bottles on the roof
   * of a randomly placed container (roofLines.ts). Default: the segment's point `spawnPointId`.
   */
  readonly point?: CollectibleSpawnPoint;
}

export interface DecorationPlacement {
  readonly socketId: string;
  readonly assetId: AssetId;
  /** Added to the socket yaw (radians). */
  readonly yawOffset?: number;
  /** Backdrop pieces: world space they claim (checked when the segment is shown). */
  readonly spacing?: DecorationSpacing;
}

export interface SpawnPlan {
  readonly obstacles: readonly ObstaclePlacement[];
  readonly collectibles: readonly CollectiblePlacement[];
  readonly decorations: readonly DecorationPlacement[];
  /** Ids of the segment's moving hazards that take part (validated like obstacle rows). */
  readonly hazards: readonly string[];
}

export const EMPTY_SPAWN_PLAN: SpawnPlan = { obstacles: [], collectibles: [], decorations: [], hazards: [] };
